import { motion } from 'framer-motion';
import { Users, Target, Zap, Globe, Award, Heart } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const values = [
  {
    icon: Zap,
    title: 'Innovation',
    description: 'We push the boundaries of AI technology to create solutions that truly make a difference.',
  },
  {
    icon: Users,
    title: 'Customer First',
    description: 'Every decision we make is guided by how it will help our customers succeed.',
  },
  {
    icon: Heart,
    title: 'Integrity',
    description: 'We build trust through transparency, honesty, and ethical AI practices.',
  },
  {
    icon: Globe,
    title: 'Accessibility',
    description: 'We believe powerful AI tools should be accessible to businesses of all sizes.',
  },
];

const team = [
  { name: 'Alex Chen', role: 'CEO & Co-founder', avatar: '👨‍💼' },
  { name: 'Sarah Miller', role: 'CTO & Co-founder', avatar: '👩‍💻' },
  { name: 'David Park', role: 'Head of AI', avatar: '🧑‍🔬' },
  { name: 'Emily Johnson', role: 'Head of Product', avatar: '👩‍🎨' },
];

export default function About() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title="About SweetPilot"
      subtitle="Building the future of work with AI employees that never sleep."
    >
      {/* Mission Section */}
      <section className="mb-16">
        <div className={`p-8 rounded-2xl border ${
          isDark
            ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
            : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
        }`}>
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Our Mission</h2>
          <p className={`text-lg leading-relaxed ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
            At SweetPilot, we're on a mission to democratize AI for businesses worldwide. We believe that every
            company, regardless of size, deserves access to powerful AI assistants that can handle marketing,
            sales, customer support, and more — 24 hours a day, 7 days a week.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>Our Story</h2>
        <div className={`space-y-4 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
          <p>
            SweetPilot was founded in 2024 with a simple observation: small and medium businesses were struggling
            to compete with larger enterprises that could afford dedicated teams for every function.
          </p>
          <p>
            We asked ourselves: what if AI could level the playing field? What if a solo entrepreneur could
            have access to the same quality of marketing, sales, and support as a Fortune 500 company?
          </p>
          <p>
            That question led us to build SweetPilot — a platform where businesses can hire AI employees that
            work tirelessly around the clock, never take sick days, and continuously learn and improve.
          </p>
          <p>
            Today, over 50,000 businesses trust SweetPilot to handle critical tasks, saving thousands of hours
            and millions of dollars in operational costs.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>Our Values</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-6 rounded-2xl border ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
                <value.icon className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{value.title}</h3>
              <p className={isDark ? 'text-gray-400' : 'text-slate-500'}>{value.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>Leadership Team</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {team.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className={`text-center p-6 rounded-2xl border ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center mx-auto mb-4 text-4xl">
                {member.avatar}
              </div>
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{member.name}</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{member.role}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="mb-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { value: '50K+', label: 'Active Users' },
            { value: '15', label: 'AI Employees' },
            { value: '11M+', label: 'Tasks Completed' },
            { value: '100+', label: 'Countries' },
          ].map((stat, index) => (
            <div key={stat.label} className={`text-center p-6 rounded-2xl border ${
              isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
            }`}>
              <div className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark
          ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
          : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
      }`}>
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Want to Learn More?</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          We'd love to hear from you. Reach out to discuss partnerships, press inquiries, or just say hello.
        </p>
        <a
          href="mailto:hello@flowai.com"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
        >
          Contact Us
        </a>
      </section>
    </StaticPageLayout>
  );
}
